import { NgClass } from '@angular/common';
import { Component, EventEmitter, Input, OnChanges, OnInit, Output, SimpleChanges } from '@angular/core';
import { FormsModule } from '@angular/forms';
import { NgbDateParserFormatter, NgbDatepickerI18n, NgbDatepickerModule, NgbDateStruct } from '@ng-bootstrap/ng-bootstrap';
import { DateParserFormatter } from 'services/date-parser-formatter.service';
import { CustomDatepickerI18n } from 'src/app/i18n/custom-datepicker-i18n';

export enum DateRangeMode {
  Past = 'past',
  Future = 'future',
  FutureAndToday = 'futureAndToday',
  All = 'all'
}

@Component({
  selector: 'app-date-picker',
  standalone: true,
  imports: [FormsModule, NgbDatepickerModule, NgClass],
  templateUrl: './date-picker.component.html',
  styleUrl: './date-picker.component.scss',
  providers: [
    { provide: NgbDateParserFormatter, useClass: DateParserFormatter },
    { provide: NgbDatepickerI18n, useClass: CustomDatepickerI18n }
  ]
})
export class DatePickerComponent implements OnInit, OnChanges {
  @Input() date: string = '';
  @Input() invalid: boolean = false;
  @Input() disabled: boolean = false;
  @Input() dateRangeMode: DateRangeMode = DateRangeMode.All;
  @Output() dateSelected = new EventEmitter<string>();
  @Output() blured = new EventEmitter<void>();

  dateObject: NgbDateStruct | null = null;

  today: NgbDateStruct = {
    year: new Date().getFullYear(),
    month: new Date().getMonth() + 1,
    day: new Date().getDate()
  };

  minDate: NgbDateStruct = (() => {
    const tomorrow = new Date();
    tomorrow.setDate(tomorrow.getDate() + 1);
    return {
      year: tomorrow.getFullYear(),
      month: tomorrow.getMonth() + 1,
      day: tomorrow.getDate()
    };
  })();

  get minDateToUse(): NgbDateStruct {
    if (this.dateRangeMode === DateRangeMode.Future) {
      const tomorrow = new Date();
      tomorrow.setDate(tomorrow.getDate() + 1);
      return {
        year: tomorrow.getFullYear(),
        month: tomorrow.getMonth() + 1,
        day: tomorrow.getDate()
      };
    }

    if (this.dateRangeMode === DateRangeMode.FutureAndToday) {
      return this.today;
    }

    return { year: 1900, month: 1, day: 1 };
  }

  get maxDateToUse(): NgbDateStruct {
    if (this.dateRangeMode === DateRangeMode.Past) {
      const d = new Date();
      d.setDate(d.getDate() - 1);

      return {
        year: d.getFullYear(),
        month: d.getMonth() + 1,
        day: d.getDate()
      };
    }

    return { year: 3000, month: 1, day: 1 };
  }

  ngOnInit(): void {
    if (this.date) {
      if (this.date.includes('.')) {
        const [day, month, year] = this.date.split('.').map(Number);
        this.dateObject = { year, month, day };
      } else if (this.date.includes('-')) {
        const [year, month, day] = this.date.split('-').map(Number);
        this.dateObject = { year, month, day };
      }
    }
    this.setDateObjectFromString(this.date);
  }

  handleDateSelected(): void {
    const { day, month, year } = this.dateObject!;
    if (!this.isValidDate(this.dateObject!)) {
      return;
    }

    this.dateSelected.emit(`${year}-${month < 10 ? '0' : ''}${month}-${day < 10 ? '0' : ''}${day}`);
  }

  isValidDate(date: NgbDateStruct): boolean {
    if (!date || date.year < 1000 || date.year > 3000) return false;

    const jsDate = new Date(date.year, date.month - 1, date.day);
    const today = new Date();
    today.setHours(0, 0, 0, 0);

    const isSame = jsDate.getFullYear() === date.year && jsDate.getMonth() === date.month - 1 && jsDate.getDate() === date.day;

    if (!isSame) return false;

    switch (this.dateRangeMode) {
      case DateRangeMode.Past:
        return jsDate <= today;
      case DateRangeMode.Future:
        const tomorrow = new Date();
        tomorrow.setDate(today.getDate() + 1);
        tomorrow.setHours(0, 0, 0, 0);
        return jsDate >= tomorrow;
      case DateRangeMode.FutureAndToday:
        return jsDate >= today;
      default:
        return true;
    }
  }

  onBlur(): void {
    if (!this.dateObject || !this.isValidDate(this.dateObject)) {
      this.dateSelected.emit('');
      this.dateObject = null;
    }
    this.blured.emit();
  }

  onModelChange(model: NgbDateStruct | string | null): void {
    if (typeof model === 'string') {
      const parts = model.trim().split(/[-.]/).map(Number);

      if (parts.length === 3) {
        const [d1, d2, d3] = parts;
        const [day, month, year] = model.includes('.') ? [d1, d2, d3] : [d3, d2, d1];

        const parsedDate = { year, month, day };

        if (this.isValidDate(parsedDate)) {
          this.dateObject = parsedDate;
          this.invalid = false;
          this.handleDateSelected();
        } else {
          this.invalid = true;
        }
      } else {
        this.invalid = true;
      }
    } else if (model) {
      this.dateObject = model;
      this.invalid = false;
      this.handleDateSelected();
    } else {
      this.invalid = true;
    }
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (changes['date'] && !changes['date'].firstChange) {
      this.setDateObjectFromString(changes['date'].currentValue);
    }
  }

  private setDateObjectFromString(date: string): void {
    if (!date) {
      this.dateObject = null;
      return;
    }

    if (date.includes('.')) {
      const [day, month, year] = date.split('.').map(Number);
      this.dateObject = { year, month, day };
    } else if (date.includes('-')) {
      const [year, month, day] = date.split('-').map(Number);
      this.dateObject = { year, month, day };
    }
  }
}
